/**
 * The single empty-state primitive for every app (BL-1245, ratifying the
 * `EmptyState` row of the ADR 030 Canon).
 *
 * The merge collapses two implementations of the same job — admin-web's
 * `EmptyState` (the canonical base per ADR 030 § 3b: an optional icon with a
 * default and a `ReactNode` message) and bidder-web's `EmptyState` (which
 * contributes the `action` slot). Both drew the same shape: an icon, a title, an
 * optional supporting line, and an optional call to action, centered. They
 * differed only in VALUES — how the icon is wrapped, what the title's element
 * is, where the spacing sits, and how the action arrives.
 *
 * WHY a `dialect` object rather than a pile of `className` props: ADR 030's
 * foundation is shared but its dialects are deliberately different, and
 * `sharing.md` § 1 caps a per-app skin at a thin wrapper. One frozen object of
 * class names per app keeps the appearance a piece of DATA the skin declares
 * once, so the wrapper stays a passthrough instead of re-deriving styling on
 * every render. THREE dialects bind to this core today — admin classic, admin
 * settings-v2, and bidder — because admin already carried an internal
 * classic/v2 split before this merge (BL-916).
 *
 * WHY every slot is optional, including the wrappers: the three dialects
 * genuinely disagree about whether a wrapper exists. Admin classic puts the
 * icon's size and color directly on the icon with NO container; settings-v2 and
 * bidder both wrap it. Bidder wraps its action in a spacing div; admin carries
 * that spacing on the button itself. Making the wrappers conditional on the
 * dialect declaring them is what lets one structure serve all three without
 * adding a stray div to 26 admin surfaces.
 *
 * WHY `action` is a `ReactNode` here and not admin's `{ label, onClick, icon }`
 * object: `ReactNode` is strictly more general, and the object is *admin's
 * button vocabulary* — `<Button variant="outline">` in classic, `.ui-btn--primary`
 * in settings-v2. That belongs in admin's skin, which already imports both. The
 * core stays ignorant of any app's button, which is the one place this merge is
 * a real simplification rather than a relocation.
 *
 * WHY the element tags are dialect-controlled: the three implementations use
 * three different tags for the title (`<p>` classic, `<div>` settings-v2, `<h3>`
 * bidder). Normalizing them would change the DOM and inherit different UA
 * margins, defeating the no-visual-change promise this merge is built on.
 *
 * WHY there is no default icon: the DEFAULT belongs to the skin — admin falls
 * back to `Inbox`, bidder requires the caller to pass one. A core-level default
 * would leak an icon into a consumer that deliberately has none.
 *
 * THE ONE DELIBERATE DOM CHANGE in this merge: the icon is `aria-hidden`
 * unconditionally. Admin's settings-v2 dialect already did this; admin classic
 * and bidder did not, so those two gain it. Zero pixels move — `aria-hidden`
 * has no visual effect — and the icon here is decorative by construction: it
 * sits beside a `title` that always carries the same meaning in text, so
 * announcing it twice is noise for a screen-reader user. Preserving the
 * omission to keep the DOM byte-identical would have meant deliberately
 * carrying an accessibility defect into two of the three dialects.
 */
'use client';

import clsx from 'clsx';
import type { ElementType, ReactNode } from 'react';

/**
 * Per-app appearance binding — class names and element choices only, no structure.
 *
 * Every slot is optional: a consumer with no dialect gets the bare accessible
 * structure, which is the right default for a fourth app that hasn't declared
 * one yet.
 */
export interface EmptyStateDialect {
    /** The outer block — layout, padding, alignment, base text treatment. */
    root?: string;
    /**
     * Optional wrapper around the icon.
     *
     * OMIT IT to render the icon as a direct child of the root (admin classic,
     * which sizes and colors the icon itself). Provide it for a dialect that
     * draws a container around the icon — settings-v2's `.ui-empty__glyph`, or
     * bidder's rounded grey badge.
     */
    glyph?: string;
    /** Classes applied to the icon element itself. */
    icon?: string;
    /**
     * Stroke weight forwarded to the icon as a PROP.
     *
     * Lives here rather than in `icon` because it is not expressible as a class:
     * lucide reads `strokeWidth` off props. Bidder's 1.5 is visibly lighter than
     * lucide's default of 2, so a class-only dialect would silently lose it.
     */
    iconStrokeWidth?: number;
    /** Classes on the title element. */
    title?: string;
    /** Classes on the message element. */
    message?: string;
    /**
     * Optional wrapper around the action node.
     *
     * OMIT IT when the dialect's action carries its own spacing (both admin
     * dialects do). Provide it for a dialect that separates the action with a
     * container — bidder's `mt-6`.
     */
    action?: string;
    /** Element the title renders as. Defaults to `p`. */
    titleAs?: 'p' | 'h3' | 'div';
    /** Element the message renders as. Defaults to `p`. */
    messageAs?: 'p' | 'div';
}

/**
 * Props for the shared EmptyState primitive.
 */
export interface EmptyStateProps {
    /** Icon component. Omit for a text-only empty state; skins supply their own default. */
    icon?: ElementType;
    /** Primary heading text. */
    title: string;
    /** Optional supporting line. Accepts rich content, e.g. an emphasized word. */
    message?: ReactNode;
    /**
     * Optional call to action, as a fully-formed node.
     *
     * Skins that expose a structured action (admin's `{ label, onClick, icon }`)
     * render it into their own button here — see the file header.
     */
    action?: ReactNode;
    /** Per-app appearance binding. */
    dialect?: EmptyStateDialect;
    /** Extra classes on the root, composed with `dialect.root`. */
    className?: string;
}

/**
 * Whether an optional slot should render its wrapper element.
 *
 * The rule: render the wrapper iff the value would actually SHOW something.
 * React itself renders nothing for `null`, `undefined`, either boolean, and the
 * empty string — so those are exactly what this excludes. Everything else,
 * including `0`, produces visible output and earns its wrapper.
 *
 * WHY not a plain `&&` truthiness check (what the pre-merge components used):
 * `0` is a legitimate renderable here (a caller can pass `message={count}`), and
 * `{0 && <p>…</p>}` evaluates to `0`, which React renders as a bare `"0"` text
 * node OUTSIDE the wrapper — the `0`-is-falsy trap `company-standards.md`
 * bans.
 *
 * WHY not a plain `!= null` check either: that lets `false` AND `''` through, so
 * `message={cond && 'text'}` (the common conditional idiom) and `message={name}`
 * where `name` is `''` both render an EMPTY `<p>` — or, for the action slot, an
 * empty spacing div. That stray gap is exactly what RAC 3 targets.
 *
 * The one deliberate divergence from the pre-merge `&&`: a literal `true` used
 * to render an empty wrapper and now renders nothing. `message={true}` is a
 * nonsense input either way, and an empty `<p>` is the bug, not the contract.
 */
function isRenderable(value: ReactNode): boolean {
    return value != null && typeof value !== 'boolean' && value !== '';
}

/**
 * Placeholder shown when a list, table, or section has no items.
 *
 * @param props - {@link EmptyStateProps}
 * @returns The rendered empty state in the supplied dialect.
 */
export default function EmptyState({
    icon: Icon,
    title,
    message,
    action,
    dialect = {},
    className,
}: EmptyStateProps) {
    const Title = dialect.titleAs ?? 'p';
    const Message = dialect.messageAs ?? 'p';

    // Built once so the "wrapped vs bare" decision below reads as one choice
    // rather than duplicating the icon element in two branches.
    const iconNode = Icon ? (
        <Icon
            className={dialect.icon}
            {...(dialect.iconStrokeWidth != null ? { strokeWidth: dialect.iconStrokeWidth } : {})}
            aria-hidden
        />
    ) : null;

    return (
        <div className={clsx(dialect.root, className)}>
            {iconNode && (dialect.glyph ? <div className={dialect.glyph}>{iconNode}</div> : iconNode)}
            <Title className={dialect.title}>{title}</Title>
            {isRenderable(message) && <Message className={dialect.message}>{message}</Message>}
            {isRenderable(action) && (dialect.action ? <div className={dialect.action}>{action}</div> : action)}
        </div>
    );
}
